import React, {createContext, useContext, useState, useCallback, PropsWithChildren} from "react";

/**
 * Report types that can be exported
 */
export type ExportReportType =
	| "completedLessons"
	| "completedCourses"
	| "activeUsers"
	| "inactiveUsers"
	| "timeInTraining"
	| "usersWithOverdueLessons";

/**
 * Export task state
 */
interface ExportTask {
	reportType: ExportReportType;
	startedAt: number;
}

/**
 * Export Team Progress Context
 * 
 * Manages the state of export tasks to prevent multiple simultaneous exports
 * and track which reports are currently being generated.
 */
interface ExportTeamProgressContextValue {
	/**
	 * List of currently running export tasks
	 */
	exportTasks: ExportTask[];

	/**
	 * Add a new export task
	 */
	addExportTask: (reportType: ExportReportType) => void;

	/**
	 * Remove a completed export task
	 */
	removeExportTask: (reportType: ExportReportType) => void;

	/**
	 * Check if a specific report type is currently exporting
	 */
	exportTaskExists: (reportType: ExportReportType) => boolean;

	/**
	 * Check if any exports are currently running
	 */
	exportTasksRunning: () => boolean;
}

const ExportTeamProgressContext = createContext<ExportTeamProgressContextValue | undefined>(
	undefined
);

/**
 * Export Team Progress Provider
 * 
 * Provides export state management for the Team Progress page.
 * Prevents multiple simultaneous exports (one at a time policy).
 */
export function ExportTeamProgressProvider({children}: PropsWithChildren) {
	const [exportTasks, setExportTasks] = useState<ExportTask[]>([]);

	const addExportTask = useCallback((reportType: ExportReportType) => {
		setExportTasks((prev) => [
			...prev,
			{
				reportType,
				startedAt: Date.now(),
			},
		]);
	}, []);

	const removeExportTask = useCallback((reportType: ExportReportType) => {
		setExportTasks((prev) => prev.filter((task) => task.reportType !== reportType));
	}, []);

	const exportTaskExists = useCallback(
		(reportType: ExportReportType) => {
			return exportTasks.some((task) => task.reportType === reportType);
		},
		[exportTasks]
	);

	const exportTasksRunning = useCallback(() => {
		return exportTasks.length > 0;
	}, [exportTasks]);

	const value: ExportTeamProgressContextValue = {
		exportTasks,
		addExportTask,
		removeExportTask,
		exportTaskExists,
		exportTasksRunning,
	};

	return (
		<ExportTeamProgressContext.Provider value={value}>
			{children}
		</ExportTeamProgressContext.Provider>
	);
}

/**
 * Hook to access export context
 */
export function useExportTeamProgressContext() {
	const context = useContext(ExportTeamProgressContext);
	if (!context) {
		throw new Error(
			"useExportTeamProgressContext must be used within ExportTeamProgressProvider"
		);
	}
	return context;
}
